/**
 * InfinitSales - WhatsApp Service
 * Service for WhatsApp API communication
 * 
 * UPDATED: Added sendMessageWithMedia for text + multiple files
 */

import api from '../global/api';

const WHATSAPP_BASE = '/api/whatsapp';

/**
 * WhatsApp Service Class
 */
class WhatsAppService {
  // ============================================
  // CONNECTION
  // ============================================

  /**
   * Get connection status
   */
  async getConnectionStatus() {
    const response = await api.get(`${WHATSAPP_BASE}/status`);
    return response.data;
  }

  /**
   * Get QR code for connection
   */
  async getQRCode() {
    const response = await api.get(`${WHATSAPP_BASE}/qrcode`);
    return response.data;
  }

  /**
   * Disconnect WhatsApp
   */
  async disconnect() {
    const response = await api.post(`${WHATSAPP_BASE}/disconnect`);
    return response.data;
  }

  /**
   * Reconnect WhatsApp (restart + new QR)
   */
  async reconnect() {
    const response = await api.post(`${WHATSAPP_BASE}/reconnect`);
    return response.data;
  }

  /**
   * Recreate WhatsApp instance from scratch
   * 
   * Use when instance is in error state and cannot generate QR code.
   * This will delete the existing instance and create a new one.
   * 
   * @returns {Promise<Object>} Result with QR code data
   */
  async recreateInstance(provider = null) {
    console.log('[WhatsApp] Recreating instance...', provider ? `provider=${provider}` : '');
    const response = await api.post(`${WHATSAPP_BASE}/recreate-instance`, provider ? { provider } : {});
    return response.data;
  }

  /**
   * Provedores de WhatsApp QR disponiveis para a tela de conexao
   * (qual e o padrao, quais alternativas existem, qual esta em uso).
   */
  async getProviders() {
    const response = await api.get(`${WHATSAPP_BASE}/providers`);
    return response.data;
  }

  // ============================================
  // CHATS
  // ============================================

  /**
   * List paginated chats (most recent first).
   * @param {number} limit  - default 50, max 200
   * @param {number} offset - default 0
   * @param {string[]|null} labelIds - filtra por etiqueta/funil (OR). Filtro
   *   aplicado server-side ANTES da paginação (puxa tudo, não só a página).
   * @returns {{ data, count, total, has_more }}
   */
  async getChats(limit = 50, offset = 0, labelIds = null, extra = null) {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    if (Array.isArray(labelIds)) {
      labelIds.forEach((id) => { if (id) params.append('label_ids', id); });
    }
    if (extra) {
      if (extra.member_id) params.set('member_id', extra.member_id);
      if (extra.since) params.set('since', extra.since);
      if (extra.created_from) params.set('created_from', extra.created_from);
      if (extra.created_to) params.set('created_to', extra.created_to);
      if (extra.unanswered) params.set('unanswered', 'true');
      if (extra.unread) params.set('unread', 'true');
      // Busca DENTRO dos filtros (inbox_search_within_filters): o Inbox so
      // manda o termo com filtro ativo; o servidor aplica como intersecao.
      if (extra.q) params.set('q', extra.q);
      if (extra.q && extra.search_messages) params.set('search_messages', 'true');
      if (Array.isArray(extra.custom_fields)) {
        // Repetível: ?cf=campo:valor&cf=outro:valor. Formato de fio do motor
        // core (services/custom_field_search.py), igual nos 4 canais.
        extra.custom_fields.forEach(({ field_id, value }) => {
          if (field_id && value) params.append('cf', `${field_id}:${value}`);
        });
      }
    }
    const response = await api.get(`${WHATSAPP_BASE}/chats?${params.toString()}`);
    return response.data;
  }

  /**
   * Busca server-side nos contatos (nome ou telefone) — acha lead que NÃO está
   * na página carregada do inbox (migrado/sem mensagem). Retorna { data, count }.
   * @param {string} q termo de busca
   */
  async searchChats(q, searchMessages = false, customFields = null) {
    const params = new URLSearchParams();
    params.set('q', q);
    if (searchMessages) params.set('search_messages', 'true');
    (customFields || []).forEach(({ field_id, value }) => {
      if (field_id && value) params.append('cf', `${field_id}:${value}`);
    });
    const response = await api.get(`${WHATSAPP_BASE}/chats/search?${params.toString()}`);
    return response.data;
  }

  /**
   * Get messages from a chat
   * @param {string} remoteJid - Chat JID
   * @param {number} limit - Max messages to return
   */
  async getChatMessages(remoteJid, limit = 50, { signal, offset = 0 } = {}) {
    // signal opcional: AbortSignal para cancelar o fetch quando o operador
    // troca de chat antes da resposta chegar (evita misturar mensagens de
    // chats diferentes no setMessages do ChatWindow).
    // offset: paginacao por scroll up (carrega mais historico ao topo).
    const encodedJid = encodeURIComponent(remoteJid);
    const response = await api.get(
      `${WHATSAPP_BASE}/chats/${encodedJid}/messages?limit=${limit}&offset=${offset}`,
      { signal }
    );
    return response.data;
  }

  // ============================================
  // MESSAGES
  // ============================================

  /**
   * Send text message
   * @param {string} number - Phone number
   * @param {string} text - Message text
   */
  async sendMessage(number, text, { quotedMessageId = null } = {}) {
    const response = await api.post(`${WHATSAPP_BASE}/messages/send`, {
      number,
      text,
      // Responder citando (MESSAGE_ACTIONS_ENABLED): id da mensagem citada.
      ...(quotedMessageId ? { quoted_message_id: quotedMessageId } : {}),
    });
    return response.data;
  }

  /**
   * Reage (emoji vazio remove) a uma mensagem. `number` roteia o membro para
   * a conexão de origem quando a mensagem vive na partição do dono.
   */
  async sendReaction(messageId, emoji, number = null) {
    const response = await api.post(`${WHATSAPP_BASE}/messages/${encodeURIComponent(messageId)}/reaction`, {
      emoji: emoji || null,
      number: number || null,
    });
    return response.data;
  }

  /** DIV-19 (2026-10-20): apagar para todos a propria mensagem. */
  async deleteMessageForEveryone(messageId, number = null) {
    const response = await api.post(`${WHATSAPP_BASE}/messages/${encodeURIComponent(messageId)}/delete-for-everyone`, {
      number: number || null,
    });
    return response.data;
  }

  /** Edita a própria mensagem (janela de 15 min do WhatsApp). */
  async editMessage(messageId, text, number = null) {
    const response = await api.post(`${WHATSAPP_BASE}/messages/${encodeURIComponent(messageId)}/edit`, {
      text,
      number: number || null,
    });
    return response.data;
  }

  /**
   * Send media message (single file)
   * @param {string} number - Phone number
   * @param {string} mediaType - image, video, document, audio
   * @param {string} mediaUrl - URL of the media
   * @param {string} caption - Optional caption
   * @param {string} fileName - Optional filename for documents
   */
  async sendMedia(number, mediaType, mediaUrl, caption = null, fileName = null, { quotedMessageId = null } = {}) {
    const response = await api.post(`${WHATSAPP_BASE}/messages/send-media`, {
      number,
      media_type: mediaType,
      media_url: mediaUrl,
      caption,
      file_name: fileName,
      ...(quotedMessageId ? { quoted_message_id: quotedMessageId } : {}),
    });
    return response.data;
  }

  /**
   * Send message with multiple file attachments
   * 
   * This is the primary method for sending messages from AI Workspace
   * that may include multiple files (PDFs, images, videos, etc).
   * 
   * @param {string} number - Phone number (with country code, no +)
   * @param {string} text - Optional text message
   * @param {Array} files - Array of file objects
   * @param {string} files[].url - File URL (Azure Blob Storage)
   * @param {string} files[].type - File type: image, video, audio, document, pdf
   * @param {string} files[].filename - Original filename
   * @param {string} files[].mimeType - Optional MIME type
   * 
   * @returns {Object} Result with success status, message IDs, file results
   * 
   * @example
   * // Send text only
   * await sendMessageWithMedia('5511999999999', 'Hello!', []);
   * 
   * @example
   * // Send files only
   * await sendMessageWithMedia('5511999999999', '', [
   *   { url: 'https://...', type: 'pdf', filename: 'doc.pdf' }
   * ]);
   * 
   * @example
   * // Send text + files
   * await sendMessageWithMedia('5511999999999', 'Here are the docs:', [
   *   { url: 'https://...', type: 'pdf', filename: 'proposal.pdf' },
   *   { url: 'https://...', type: 'image', filename: 'chart.png' }
   * ]);
   */
  async sendMessageWithMedia(number, text, files = [], { quotedMessageId = null } = {}) {
    // Validate: must have text or files
    const hasText = text && text.trim();
    const hasFiles = files && files.length > 0;
    
    if (!hasText && !hasFiles) {
      throw new Error('Must provide text or files (or both)');
    }

    // Prepare files payload
    const filesPayload = hasFiles ? files.map(f => ({
      url: f.url,
      type: f.type || 'document',  // Default to document
      filename: f.filename || this._extractFilename(f.url),
      mimeType: f.mimeType || null,
      // Escolha "Documento" no menu de anexo. Precisa chegar ao backend: lá o
      // mimetype real (video/quicktime) venceria o type e o arquivo voltaria a
      // ser tratado como vídeo, batendo no limite de 16MB.
      asDocument: !!f.asDocument,
    })) : [];

    console.log('[WhatsAppService] sendMessageWithMedia:', {
      number,
      textLength: text?.length || 0,
      filesCount: filesPayload.length,
      files: filesPayload.map(f => ({ filename: f.filename, type: f.type })),
    });

    const response = await api.post(`${WHATSAPP_BASE}/messages/send-with-media`, {
      number,
      text: hasText ? text : null,
      files: filesPayload,
      ...(quotedMessageId ? { quoted_message_id: quotedMessageId } : {}),
    });

    return response.data;
  }

  /**
   * Mark messages as read
   * @param {string} remoteJid - Chat JID
   * @param {string} messageId - Message ID to mark as read
   */
  async markAsRead(remoteJid, messageId) {
    const response = await api.post(`${WHATSAPP_BASE}/messages/mark-read`, {
      remote_jid: remoteJid,
      message_id: messageId,
    });
    return response.data;
  }

  /**
   * Send typing indicator
   * @param {string} number - Phone number
   * @param {boolean} typing - True to start, false to stop
   */
  async sendTyping(number, typing = true) {
    const response = await api.post(
      `${WHATSAPP_BASE}/messages/typing?number=${number}&typing=${typing}`
    );
    return response.data;
  }

  // ============================================
  // CONTACTS
  // ============================================

  /**
   * List all contacts
   */
  async getContacts() {
    const response = await api.get(`${WHATSAPP_BASE}/contacts`);
    return response.data;
  }

  /**
   * Get profile picture URL
   * @param {string} number - Phone number
   */
  async getProfilePicture(number) {
    const response = await api.get(`${WHATSAPP_BASE}/contacts/${number}/picture`);
    return response.data;
  }

  /**
   * Check if numbers are on WhatsApp
   * @param {Array} numbers - List of phone numbers
   */
  async checkWhatsAppNumbers(numbers) {
    const response = await api.post(`${WHATSAPP_BASE}/contacts/check`, {
      numbers,
    });
    return response.data;
  }

  // ============================================
  // GROUPS
  // ============================================

  /**
   * List all groups
   */
  async getGroups() {
    const response = await api.get(`${WHATSAPP_BASE}/groups`);
    return response.data;
  }

  /**
   * Get group info
   * @param {string} groupJid - Group JID
   */
  async getGroupInfo(groupJid) {
    const encodedJid = encodeURIComponent(groupJid);
    const response = await api.get(`${WHATSAPP_BASE}/groups/${encodedJid}`);
    return response.data;
  }

  /**
   * Atualiza manualmente o pushName de um contato (override do operador).
   * O backend marca push_name_edited_by_user=True para impedir que webhooks
   * futuros sobrescrevam a edicao.
   * @param {string} number - Telefone (digits only ou com formatacao)
   * @param {string} pushName - Novo nome
   */
  async updateContactPushName(number, pushName) {
    const cleanNumber = (number || '').replace(/[^0-9]/g, '');
    const response = await api.patch(
      `${WHATSAPP_BASE}/contacts/${cleanNumber}/push-name`,
      { push_name: pushName }
    );
    return response.data;
  }

  // ============================================
  // UTILITIES
  // ============================================

  /**
   * Extract phone number from JID
   * @param {string} jid - WhatsApp JID
   */
  extractNumber(jid) {
    if (!jid) return '';
    return jid.split('@')[0];
  }

  /**
   * Check if JID is a group
   * @param {string} jid - WhatsApp JID
   */
  isGroup(jid) {
    return jid?.includes('@g.us') || false;
  }

  /**
   * Format phone number (remove special chars)
   * @param {string} number - Phone number
   */
  formatNumber(number) {
    if (!number) return '';
    return number.replace(/[^0-9]/g, '');
  }

  /**
   * Extract filename from URL
   * @private
   * @param {string} url - File URL
   */
  _extractFilename(url) {
    if (!url) return 'arquivo';
    try {
      const cleanUrl = url.split('?')[0];
      const filename = cleanUrl.split('/').pop();
      return decodeURIComponent(filename) || 'arquivo';
    } catch {
      return 'arquivo';
    }
  }

  // ============================================
  // MEDIA DOWNLOAD
  // ============================================

  /**
   * Download media from a message
   * 
   * Uses Evolution API's getBase64FromMediaMessage to retrieve
   * the full media content as base64.
   * 
   * @param {string} messageKeyId - The key.id from the message
   * @param {boolean} convertToMp4 - Convert video to MP4
   * @returns {Promise<Object>} Media data with base64, mimetype, fileName
   */
  async downloadMedia(messageKeyId, convertToMp4 = false) {
    if (!messageKeyId) {
      throw new Error('messageKeyId is required');
    }

    console.log('[WhatsApp] Downloading media:', messageKeyId);

    try {
      const response = await api.post(`${WHATSAPP_BASE}/media/download`, {
        message_key_id: messageKeyId,
        convert_to_mp4: convertToMp4,
      });

      if (!response.data?.success) {
        throw new Error(response.data?.detail || 'Download failed');
      }

      const data = response.data.data;
      console.log('[WhatsApp] Media downloaded:', {
        type: data.mediaType,
        fileName: data.fileName,
        mimetype: data.mimetype,
      });

      return data;
    } catch (error) {
      console.error('[WhatsApp] Media download error:', error);
      // Propaga a mensagem ACIONÁVEL do backend (ex.: "Mídia indisponível:
      // a mensagem não está mais no armazenamento do WhatsApp") — sem isto a
      // UI mostrava "Request failed with status code 404/500".
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === 'string') {
        const friendly = new Error(detail);
        friendly.status = error?.response?.status;
        throw friendly;
      }
      throw error;
    }
  }

  /**
   * Download media from a message object
   * Convenience method that extracts the key.id automatically
   * 
   * @param {Object} messageKey - The message.key object
   * @param {string} mediaType - Optional: force media type (image, audio, video, document)
   * @returns {Promise<Object>} Media data with base64, mimetype, fileName
   */
  async downloadMediaFromMessage(messageKey, mediaType = null) {
    if (!messageKey?.id) {
      throw new Error('Invalid message key');
    }

    // For videos, convert to MP4 for better compatibility
    const convertToMp4 = mediaType === 'video';

    return this.downloadMedia(messageKey.id, convertToMp4);
  }
}

// Export singleton instance
const whatsappService = new WhatsAppService();
export default whatsappService;